import React from "react";
import { ArrowUpRight, CreditCard, ExternalLink, FileText, Loader2, Lock, Plus } from "lucide-react";
import PlanHero, {
  HERO_BTN, HeroBar, HeroFigure, HeroStats, NextCharge, PaymentMethodRow,
} from "../../../Account/billing/PlanHero";
import {
  TRIAL_PLAN_KEY, statusMeta, canStartCheckout, isManualPlan, fmtNum, fmtCredits, fmtDate, fmtPeriod, fmtUsd, INTERVAL_LABEL,
} from "./billingConst";

// statusMeta tones, as the plan card's chip colours.
const CHIP_TONE = { ok: "ok", warn: "attention", info: "attention", danger: "danger", neutral: "muted" };
const PER_INTERVAL = { monthly: " / month", annual: " / year" };

/* The account's plan, live balance and the primary billing actions, on the
   same card as RFP Manager's plan. Every action is gated on the booleans the
   balance endpoint hands back (can_buy_addons, cancel_at_period_end, …)
   rather than re-deriving them from status, which is what the backend asks
   callers to do. `nextPlan` is a plan change scheduled for the renewal. */
const SubscriptionHero = ({ balance, nextPlan, busy, onPortal, onBuyCredits, onViewPlans, onCancel }) => {
  const status = balance.status;
  const ending = balance.cancel_at_period_end;
  const isTrial = balance.plan_key === TRIAL_PLAN_KEY;
  // A plan given by hand never charges, so there's no failed payment, renewal, card or invoice.
  const manual = isManualPlan(balance);
  const needsPayment = (status === "payment_required" || status === "paused") && !manual;
  const cancellable = status === "active" && !isTrial && !ending;
  const renews = cancellable && !manual;
  // A trial has no Stripe customer yet, so there's no card or invoice to show.
  const onStripe = !isTrial && !manual;
  const meta = statusMeta(status);

  const allocation = Number(balance.allocation_credits ?? balance.monthly_credits ?? 0);
  const remaining = Number(balance.balance ?? balance.credits ?? 0);
  // Trust used_this_period — it comes from the ledger, not from subtracting a
  // balance that add-on packs and carryover both move independently.
  const used = Number(balance.used_this_period ?? Math.max(allocation - remaining, 0));
  const usedPct = allocation > 0 ? Math.min(Math.round((used / allocation) * 100), 100) : 0;

  const nextPrice = nextPlan?.price_usd ?? balance.price_usd;
  const [chargeAmount, chargeNote] = renews && nextPrice
    ? [fmtUsd(nextPrice), `on ${fmtDate(balance.period_end)}${nextPlan ? ` · ${nextPlan.name}` : ""}`]
    : needsPayment && balance.price_usd
      ? [fmtUsd(balance.price_usd), "renewal payment failed"]
      : ["$0", "No upcoming charges"];

  const stats = [
    [isTrial ? "Trial credits" : "Plan allocation",
      allocation > 0 ? `${fmtNum(allocation)}${PER_INTERVAL[balance.billing_interval] || ""}` : "—"],
    ["Billing",
      balance.price_usd ? `${fmtUsd(balance.price_usd)} ${INTERVAL_LABEL[balance.billing_interval] || ""}`.trim() : "No charge"],
    ["Current period", fmtPeriod(balance.period_start, balance.period_end)],
    isTrial ? ["Renewal", "Never — one-time trial"]
      : ending ? ["Access ends", fmtDate(balance.period_end)]
      : needsPayment ? ["Extraction", balance.can_extract ? "Available" : "Disabled"]
      : status === "active" ? [manual ? "Plan ends" : "Next renewal", fmtDate(balance.period_end)]
      : null,
  ].filter(Boolean);

  const portalSpinner = busy === "portal" && <Loader2 size={15} className="animate-spin" />;

  return (
    <PlanHero
      tone={ending ? "muted" : CHIP_TONE[meta.tone]}
      label={ending ? `Ends ${fmtDate(balance.period_end)}` : meta.label}
      aside={
        <>
          <NextCharge label="Next charge" amount={chargeAmount} note={chargeNote} />

          <div className="border-t border-[#ECE9EE] pt-3.5">
            <p className="mb-2.5 text-[13px] font-semibold text-[#1A1A1A]">Payment method</p>
            <PaymentMethodRow
              empty={
                <div>
                  <p className="text-[13.5px] font-medium text-[#1A1A1A]">
                    {onStripe ? "Payment card" : "No card on file"}
                  </p>
                  <p className="text-[12px] text-[#8A8A90]">
                    {onStripe ? "Managed securely in Stripe" : "Added when you choose a plan"}
                  </p>
                </div>
              }
              onUpdate={onStripe ? onPortal : undefined}
              disabled={!!busy}
              busy={busy === "portal"}
            />
          </div>

          {onStripe && (
            <button
              type="button"
              onClick={onPortal}
              disabled={!!busy}
              className="flex w-full items-center gap-3 rounded-xl border border-[#ECE9EE] p-3 text-left transition-colors hover:bg-[#FAF9FB] disabled:opacity-50"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[8px] bg-[#FBF5FA] text-[#A04A97]">
                <FileText size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-[#1A1A1A]">Invoices &amp; billing details</p>
                <p className="text-[12px] text-[#8A8A90]">Download invoices, update address and tax IDs</p>
              </div>
              <ExternalLink size={14} className="flex-shrink-0 text-[#8A8A90]" />
            </button>
          )}

          <p className="mt-auto flex items-center gap-1.5 pt-2 text-[11.5px] text-[#8A8A90]">
            <Lock size={12} /> Payments secured by Stripe · billed in USD
          </p>
        </>
      }
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h3 className="text-[28px] font-semibold leading-tight">{balance.plan_name || "No plan"}</h3>
        <span className="text-[14px] text-white/72">
          {isTrial || !balance.billing_interval ? "Extractor Studio" : `Extractor Studio · ${balance.billing_interval}`}
        </span>
      </div>

      <div>
        <HeroFigure value={fmtNum(remaining)} caption={isTrial ? "trial credits remaining" : "credits remaining"} />
        <div className="mt-5">
          <HeroBar pct={usedPct} />
        </div>
        <div className="mt-2 flex flex-wrap justify-between gap-x-3 text-[12.5px] text-white/72">
          <span>{usedPct}% used</span>
          <span>
            {fmtCredits(used)}{allocation > 0 ? ` of ${fmtCredits(allocation)}` : ""}{" "}
            {isTrial ? "trial credits used" : "plan credits used this period"}
          </span>
        </div>
      </div>

      <HeroStats stats={stats} minWidth={140} />

      <div className="mt-1 flex flex-wrap items-center gap-3">
        {needsPayment ? (
          <button type="button" className={HERO_BTN.primary} disabled={!!busy} onClick={onPortal}>
            {portalSpinner || <CreditCard size={15} />} Update payment method
          </button>
        ) : (
          <>
            {balance.can_buy_addons && (
              <button type="button" className={HERO_BTN.primary} disabled={!!busy} onClick={onBuyCredits}>
                <Plus size={15} /> Buy credits
              </button>
            )}
            <button
              type="button"
              className={balance.can_buy_addons ? HERO_BTN.outline : HERO_BTN.primary}
              onClick={onViewPlans}
            >
              <ArrowUpRight size={15} /> {canStartCheckout(balance) ? "Choose a plan" : "Change plan"}
            </button>
          </>
        )}
        {/* Cancelling stays understated: a text link, not a button competing with the plan actions. */}
        {cancellable && (
          <button type="button" className={`${HERO_BTN.link} ml-1`} disabled={!!busy} onClick={onCancel}>
            Cancel subscription
          </button>
        )}
      </div>
    </PlanHero>
  );
};

export default SubscriptionHero;
